import type {
	MimeTypeExtended,
	TemplateSet,
} from './app.js';
import type * as DB from './database.js';

export const PHOTO_DETECTED_FOR_LIMITED_COLOR_LOGO_ERROR_CODE = 'PHOTO_DETECTED_FOR_LIMITED_COLOR_LOGO';
export const INVALID_OR_CORRUPT_FILE_ERROR_CODE = 'INVALID_OR_CORRUPT_FILE';

export const LOGO_IMAGE_TOO_LARGE_ERROR_CODE = 'LOGO_IMAGE_TOO_LARGE';
export const LOGO_IMAGE_DIMENSIONS_TOO_LARGE_ERROR_CODE = 'LOGO_IMAGE_DIMENSIONS_TOO_LARGE';
export const LOGO_TOO_MANY_CLIP_PATHS_ERROR_CODE = 'LOGO_TOO_MANY_CLIP_PATHS';
export const LOGO_TOO_MANY_EMBEDDED_RASTERS_ERROR_CODE = 'LOGO_TOO_MANY_EMBEDDED_RASTERS';
export const LOGO_TOO_MANY_GRADIENT_STOPS_ERROR_CODE = 'LOGO_TOO_MANY_GRADIENT_STOPS';
export const LOGO_TOO_DETAILED_ERROR_CODE = 'LOGO_TOO_DETAILED';
export const LOGO_TOO_COMPLEX_ERROR_CODE = 'LOGO_TOO_COMPLEX';

/**
 * Codes returned by process-photo when a logo exceeds a supported processing-
 * complexity limit. Each maps to a specific user-facing message.
 */
export const LOGO_TOO_COMPLEX_ERROR_CODES = [
	LOGO_IMAGE_TOO_LARGE_ERROR_CODE,
	LOGO_IMAGE_DIMENSIONS_TOO_LARGE_ERROR_CODE,
	LOGO_TOO_MANY_CLIP_PATHS_ERROR_CODE,
	LOGO_TOO_MANY_EMBEDDED_RASTERS_ERROR_CODE,
	LOGO_TOO_MANY_GRADIENT_STOPS_ERROR_CODE,
	LOGO_TOO_DETAILED_ERROR_CODE,
	LOGO_TOO_COMPLEX_ERROR_CODE,
] as const;

export const PHOTO_PROCESSING_ERROR_CODES = [
	INVALID_OR_CORRUPT_FILE_ERROR_CODE,
	PHOTO_DETECTED_FOR_LIMITED_COLOR_LOGO_ERROR_CODE,
	...LOGO_TOO_COMPLEX_ERROR_CODES,
] as const;

export type LogoTooComplexErrorCode = typeof LOGO_TOO_COMPLEX_ERROR_CODES[number];

export type PhotoProcessingErrorCode = typeof PHOTO_PROCESSING_ERROR_CODES[number];

export interface PhotoProcessingError extends Error {
	code?: PhotoProcessingErrorCode;
	retryable?: boolean;
}

export interface AddressModel extends DB.AddressModel {
	countryFormatted?: string;
}

export interface BookmarksModel {
	left: number | null;
	right: number | null;
}

export interface MoodSceneModel {
	/**
	 * Loaded image of the mood scene
	 */
	image: HTMLImageElement;
	/**
	 * Current version of the project which the mood scene image was generated
	 */
	projectVersion: number;
}

export type ArtworkSizeMetricUnits = 'mm' | 'in';

export interface ArtworkSizeModel {
	height: number;
	metricUnits: ArtworkSizeMetricUnits;
	width: number;
	x: number;
	y: number;
}

export interface PageModelBleedMarginSafeArea {
	/** Height of the area excluded from the editor-only bleed overlay */
	height: number;
	/** Width of the area excluded from the editor-only bleed overlay */
	width: number;
	/** Horizontal position from the full canvas origin, including visible bleed */
	x: number;
	/** Vertical position from the full canvas origin, including visible bleed */
	y: number;
}

export interface PageBackgroundPatternImage {
	/** Renderable image or canvas used as the source of a repeating pattern */
	image: CanvasImageSource;
	/** Pixel height of the renderable source */
	height: number;
	/** Pixel width of the renderable source */
	width: number;
}

export interface PageBackgroundPatternResource {
	/** Authored tile height used to determine where the pattern repeats */
	height: number;
	/** Full-resolution source retained for large editor and PageImage canvases */
	image: PageBackgroundPatternImage;
	/** Shared bounded source used by optimized thumbnail renderers */
	preview?: PageBackgroundPatternImage;
	/** Authored tile width used to determine where the pattern repeats */
	width: number;
}

export interface PageModel extends DB.PageModel {
	/** Indicator if the user has already accepted the quality warning on the page */
	_acceptedQualityWarning?: boolean;
	/** Editor-only areas kept clear while rendering an additional bleed overlay around them */
	_bleedMarginSafeAreas?: PageModelBleedMarginSafeArea[];
	/** Loaded image to apply to the page's background */
	_bgimage?: CanvasImageSource | null;
	/** Loaded pattern resource to apply to the page's background */
	_bgpattern?: PageBackgroundPatternResource | null;
	/** Indicator if the cropping of the page has been checked by the user */
	_cropped?: boolean;
	/**
	 * Indicator if the page is loading assets (background image, background pattern)
	 */
	_loading?: boolean;
	/** Indicates if template needs to be automatically switched (customLayout = false) when adding photos to the page */
	customLayout?: boolean;
	/** List of id's from page objects that are included in the page */
	objectList?: PageObjectModel['id'][];
	/** Reference to the offering model applied to this page (important for "virtual" projects) */
	offeringId?: DB.OfferingModel['id'];
	/** Show the page in portrait (p) or in landscape (l) modus */
	orientation?: 'p' | 'l';
	/** Backend fill-line code selected for this page */
	selectedFillLineCode?: string;
	/** Full active slot geometry retained when a page layout is preserved across template or size changes. */
	templateLayout?: TemplateSet | null;
	/** Combination of the template position ids that are selected as active on the page */
	templateSetId?: string | null;
	/** What margin to apply to the edge of the page when generating dynamic templates */
	templateMarginAroundEdge?: number;
	/** What margin to apply between the positions of a template when generating dynamic templates */
	templateMarginBetweenPositions?: number;
}

export interface PageObjectColorReplacementReplaceModel {
	pantoneName?: string;
	real: string;
	visual?: string;
}

export interface PageObjectColorReplacementModel {
	color: string;
	replace: PageObjectColorReplacementReplaceModel;
}

export type PageObjectColorReplacementModels = PageObjectColorReplacementModel[];

export interface PageObjectModel extends Omit<DB.PageObjectModel, 'templatestateid'>, Omit<Partial<DB.PageObjectPhotoModel>, 'id'>, Omit<Partial<DB.PageObjectTextModel>, 'id'> {
	/**
	 * Loaded border image to apply to the page object, used for drawing on the page canvas
	 */
	_borderimage?: any;
	/**
	 * Canvas element that holds the graphical representation of the page object,
	 * used for drawing on the page canvas
	 */
	_canvas?: any;
	/**
	 * Indicator if cropping modus is active on the page object
	 */
	_crop?: boolean;
	/**
	 * Indicator if there was an error loading the page object
	 */
	_error: number;
	/**
	 * Indicator if the page object is currently hovered or not
	 */
	_hovered?: boolean;
	/**
	 * Hovered vector color of the page object (if it's a vector image)
	 */
	_hoveredVectorColor?: string | null;
	/**
	 * Loaded image of the photo or source linked to the photo object or the svg image
	 * of the text object, used for drawing on the page canvas
	 */
	_image?: any;
	/**
	 * Indicate the latest version of the generated image, used to prevent race rendering
	 * conditions
	 */
	_imageVersion?: number | null;
	/** Indicates that newly inserted free text has not received a user-authored change yet */
	_isNewTextObjectUntouched?: boolean;
	/**
	 * Indicator if the page object is loading (the linked image or font)
	 */
	_loading?: any;
	/**
	 * Loaded mask image to apply to the page object
	 */
	_mask?: any;
	/**
	 * Previous state of the object, to use in conjunction with the `_rollback` property
	 */
	_previousState?: Omit<PageObjectModel, '_previousState'> | null;
	/**
	 * Indicator if the page object is currently being processed and should not be
	 * drawn yet on the canvas
	 */
	_processing?: boolean;
	/**
	 * Indicator to mark the image as needing to be reset (reloaded) from the source
	 */
	_resetImage?: boolean;
	/**
	 * Indicator if the page object is should be rolled back to the previous state,
	 * to use in conjunction with the `_previousState` property
	 */
	_rollback?: boolean;
	/**
	 * Indicator if the page object is currently being rotated
	 */
	_rotating?: boolean;
	/**
	 * Indicator if the page object is currently selected and being edited or not
	 */
	_selected?: boolean;
	/**
	 * Indicator if the page object is currently selected for edition or not.
	 * This is only being used in the new Editor, for photo objects this means
	 * that the user can delete, move, resize and rotate the photo object but not yet
	 * edit the photo itself, like adding filters or shapes (this is for what
	 * the `_selected` property is used for).
	 * For text objects this means that the user can delete, move, resize, rotate
	 * and edit the text properties (like font, color, size, etc.) but not yet
	 * edit the text itself (this is for what the `_selected` property is used for).
	 */
	_selectedForEdition?: boolean;
	/**
	 * Final SVG markup ready to be rendered for vector-based photos after color replacements and effects are applied.
	 */
	_finalVectorSVG?: string | null;
	/**
	 * Colors detected in the vector image of the page object (if it's a vector image)
	 */
	_vectorColors: PageObjectVectorColors | null;
	/**
	 * SVG content of the vector image in the page object (if it's a vector image)
	 */
	_vectorSVG: string | null;
	colorReplacement: PageObjectColorReplacementModels | null;
	/**
	 * Persisted flag indicating that background removal should be simulated
	 * for a raster logo using the vector SVG difference mask.
	 */
	rasterBackgroundRemoved?: boolean;
	/**
	 * Method to apply when filling the linked template position with the linked photo
	 * - cover: The photo is sized to maintain its aspect ratio while filling the template
	 * position's entire content box. If the photo's aspect ratio does not match the aspect
	 * ratio of the template position, then the photo will be cropped to fit.
	 * - contain: The photo is scaled to maintain its aspect ratio while fitting within
	 * the template position. The entire object is made to fill the position, while
	 * preserving its aspect ratio, so the object will be "letterboxed" if its aspect ratio
	 * does not match the aspect ratio of the box.
	 */
	fillMethod?: 'cover' | 'contain';
	templatestateid: string | number | null;
}

export interface PageObjectVectorColor {
	color: string;
	matches: string[];
}

export interface PageObjectVectorColors {
	background?: PageObjectVectorColor;
	foreground: Map<string, PageObjectVectorColor>;
}

export interface PageStateModel {
	id: number | string;
	objects: PageObjectModel[];
	pageModel: PageModel;
	repeat: 0 | 1;
	scaling: number;
	serialnumber: number;
}

export interface PhotoModel extends DB.PhotoModel {
	/** Upload-only placement context; never persisted to the photo API or project data. */
	_designPrint?: {
		targetPageIds: PageModel['id'][];
		targetPageObjectId?: PageObjectModel['id'];
		outputPhotoIds?: number[];
	};
	/** Important: must be defined when creating model to set reactivity to property */
	_error: PhotoProcessingError | undefined;
	/** Locally selected or unsaved external photos store orientation information here. Photos loaded from the S3 storage are always normalized to orientation = 1 */
	_orientation?: number;
	/** Flag to avoid duplicate parallel ajax request to the photo processing micro service */
	_processing?: boolean;
	/** For models not saved to server yet, this stores the parsed fileType (so we can check for HEIC files) */
	_type?: MimeTypeExtended;
	/** For photos selected on the device, this store the local file reference */
	_localRef?: string;
	/** Processing options forwarded for unsaved artwork uploads, including vectorization and design-print preservation */
	_vectorize?: {
		maxColors: number;
		designPrintMaxPages?: number;
		errorIfPhoto?: boolean;
		isDesignPrint?: boolean;
		skipVectorizationIfPhoto?: boolean;
	};
	/** Getter/setter indicates if the photo is being removed (uses private `_removing` prop) */
	removing?: boolean;
}

export interface PhotoModelWithRemoving extends PhotoModel {
	/** Flag to indicate if the photo is being removed  */
	_removing: boolean;
}

export interface ProductAttributeModel {
	id: string;
	property: string;
	value: string | null;
}

export interface ProductDataModel {
	/** For projects with an address (i.e. postcards), this attribute stores the address model */
	address: AddressModel;
	/**
	 * Artwork dimensions keyed by page id, calculated only for checkout snapshots of logo offerings
	 */
	artworkSizes?: Record<PageModel['id'], ArtworkSizeModel>;
	/** Cart destination supplied for this project by a SmartLink. */
	cartUrl?: string;
	/** For projects with an address (i.e. postcards), this attribute stores the country model linked to the address */
	country?: DB.CountryModel;
	/** Custom attributes send by partner when deeplinking to an offering. Need to be returned to partner when sending customer to checkout. */
	customAttributes?: Record<string, unknown>;
	/** Save meta data (such as software platforms and versions used) for the project */
	metaData?: {
		/** Named data fixes already applied; a repeated failure must remain observable. */
		appliedDataFixes?: Record<string, boolean>;
		// List of software web versions used while working on this project
		softwareWebVersions: string[];
		// List of software platforms used while working on this project
		softwarePlatforms: string[];
	};
	/** Collection of all the (photo and text) object models added to the pages in the project */
	objects: Record<PageObjectModel['id'], PageObjectModel>;
	/** Sorted list of all the ids of the pages in the project */
	pageList: PageModel['id'][];
	/** Collection of all the page models in the project */
	pages: Record<PageModel['id'], PageModel>;
	/** Project model as stored in the database */
	product: DB.ProductModel;
	/** List of all the ids of the photos included in the project */
	photoList: DB.PhotoModel['id'][];
	/** Collection of all the photo models in the project */
	photos: Record<DB.PhotoModel['id'], DB.PhotoModel>;
	/** Object that can be used to save product specific properties required for offering (i.e. baby book) */
	productAttributes: ProductAttributeModel[];
	/** Settings to apply when editing the content of the project */
	productSettings: ProductSettings;
	/** Used to set flags for product tours that have been finished by the user (to avoid showing multiple times) */
	productTours: Partial<Record<DB.ProductTours, boolean | undefined>>;
	/** Deprecated way to store product attributes (now stored under "productAttributes") */
	attributes?: ProductAttributeModel[];
	/** Deprecated way to store links models between photos and product (now stored under "photoList") */
	product_photos?: DB.ProductPhotoModel[];
	/** Deprecated way to store photo objects (now stored under "objects") */
	photoobjects?: object[];
	/** Deprecated way to store text objects (now stored under "objects") */
	textobjects?: object[];
	/** Timestamp of the latest update to the project */
	version: number | null;
	spine?: PageModel;
	/**
	 * Flag to indicate that thin lines have been detected on at least one of the pages in the project.
	 */
	thinLineDetected?: boolean;
}

export interface ProductSettings {
	/** Flag indicating if automatic enhancement and/or upscaling may be applied to photos in this project */
	applyEnhancement?: boolean;
	/** Flag inidicating if the project should automatically be filled with the selected photos */
	autoFill?: boolean;
	/** When automatically filling the project, should photo captions be added to empty text positions? */
	autoFillCaptions?: boolean;
	/** When automatically filling the project, what margin style should be used? */
	autoFillMarginId?: 'large' | 'small' | 'zero';
	/**
	 * When automatically filling the project with photos, what method should be used?
	 * photodate: Sort photos chronilogical by oldest photo first
	 * custom: Photos are manually sorted by the user
	 */
	autoFillMethod: 'photodate' | 'custom';
	/** When automatically filling the project, what photo/page density should be used? */
	autoFillPageDensityId?: 'highest' | 'high' | 'moderate' | 'fewer' | 'single';
	/** The default bgcolor for the project, to be used when adding new pages */
	bgcolor: string | null;
	/** The default bgpattern for the project, to be used when adding new pages */
	bgpattern: string | null;
	/**
	 * childOfferingTypeId is used for projects with a "virtual" offeringModel.
	 * This way we know what offering details to use for the next added page.
	 */
	childOfferingTypeId: DB.OfferingModel['typeid'] | 'bestFit' | null;
	/**
	 * childOfferingVariantId is used for projects with a "virtual" offeringModel.
	 * This way we know what offering details to use for the next added page.
	 */
	childOfferingVariantId: DB.OfferingModel['variantid'] | null;
	/** Flag inidicating if face detection should be used for cropping of photos */
	faceDetect: boolean;
	hideOfferingOptions?: boolean;
	/**
	 * When either `printColors` or `printPositions` is set, this indicates that we
	 * need to apply limitations to the user's designs and choices in the editor,
	 * but only when the current group and type ids match the ones the project was
	 * originally created with.
	 * To be able to detect and override the colors and positions, we need to store
	 * these original properties of the project, so we can compare it with the current
	 * selection at time of edition.
	 */
	originalGroupId?: DB.OfferingModel['groupid'];
	originalTypeId?: DB.OfferingModel['typeid'];
	/**
	 * Limit the available print color options when opening the creator from a smartlink
	 */
	printColors?: number | 'fc';
	/**
	 * Limit the available print positions when opening the creator from a smartlink.
	 * Print positions are `serialnumber` values of the available page models, meaning
	 * that only pages with a `serialnumber` included in this array will be available
	 * for selection in the editor.
	 * The `serialnumber` can also be translated to the page index in the `pageList` array.
	 */
	printPositions?: number[];
	/** Flag indicating that when the project is added to the cart, it should use this as the quantity parameter */
	skuQuantity?: number;
	/**
	 * How are the photos in the project sorted when adding them to the project's pages? Options:
	 * (empty string): Not sortable
	 * Normal: Photo's are sortable and popup is shown
	 * Auto: Chronologial sorting is automatically selected
	 * Manual: Manual sorting is automatically selected
	 */
	sortMode?: '' | 'Normal' | 'Auto' | 'Manual';
	/** When user starts creating the project, trigger one of the product configuration select UIs? */
	startWithOfferingOptionUI: 'size' | 'other' | null;
	/** What margin to apply to the edge of the page when generating dynamic templates */
	templateMarginAroundEdge?: number;
	/** What margin to apply between the positions of a template when generating dynamic templates */
	templateMarginBetweenPositions?: number;
}

export interface ProductStateModel {
	// Reference to the page currently being edited
	_activePage: PageModel['id'] | null;
	// Bookmarks for page indexes shown in the product preview
	_bookmarks: BookmarksModel;
	// Marker indicating the current position in the _snapshots array
	_historyIndex: number;
	// Flag to indicate if the mask is loading
	_loadingMask: boolean;
	// Flag to indicate if the overlay is loading
	_loadingOverlay: boolean;
	// Store the loaded overlay image for the product
	_mask: HTMLImageElement | null;
	// Store the loaded overlay image for the product
	_overlay: HTMLImageElement | null;
	// Flag to indicate if the product has been updated since the last save
	_saved: boolean | null;
	_snapshots: string[];
	address: DB.AddressModel | null;
	cartUrl?: string;
	customAttributes?: Record<string, unknown>;
	objects: Record<PageObjectModel['id'], PageObjectModel>;
	pageList: PageModel['id'][];
	pages: Record<PageModel['id'], PageModel>;
	photoList: (number | string)[];
	productAttributes: ProductAttributeModel[];
	productSettings: ProductSettings;
	productId: number | null;
	thinLineDetected?: boolean;
	version: number | null;
	spine: PageModel | null;
}
